import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useSession } from '../App.jsx';
import { day, qty } from '../lib/format.js';
import { parseNumber } from '../lib/numbers.js';
import { Alert, Card, CardSkeleton, Empty, Field, Pill, useAsync } from '../components/ui.jsx';
import PeriodPicker, { useUrlPeriod } from '../components/PeriodPicker.jsx';
import ExportButton, { fetchAllPages, periodSlug, periodText } from '../components/ExportButton.jsx';
import { useEntityQuery } from '../lib/entity.js';

/** Moving stock between godowns, with availability following it immediately. */
export default function Transfers() {
  const session = useSession();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') || '';
  const setStatus = (k) => setParams(k ? { status: k } : {});
  // Goods in transit and drafts are work still to finish, whenever they started.
  const entityQuery = useEntityQuery();
  const { period, setPeriod, query } = useUrlPeriod({ allByDefault: status === 'in_transit' || status === 'draft' });
  const list = useAsync(() => api.get(`/api/stock-transfers?${[status && `status=${status}`, query, entityQuery].filter(Boolean).join('&')}`), [status, query]);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(null);
  const [error, setError] = useState(null);

  const act = async (id, action) => {
    setError(null);
    try { await api.post(`/api/stock-transfers/${id}/${action}`); list.reload(); }
    catch (e) { setError(e.message); }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Stock transfers</h1>
          <div className="page-sub">
            Batch-wise movement between your own godowns. Availability follows the goods the moment they leave, and
            the transfer is flagged for a stock journal in Tally rather than left to diverge.
          </div>
        </div>
        <div className="inline">
          <div className="segmented" role="group" aria-label="View">
            {[['', 'All'], ['in_transit', 'In transit'], ['received', 'Received'], ['draft', 'Drafts']].map(([k, l]) => (
              <button key={l} aria-pressed={status === k} onClick={() => setStatus(k)}>{l}</button>
            ))}
          </div>
          <PeriodPicker allowAll value={period} onChange={setPeriod} />
          <ExportButton disabled={!list.data} build={() => ({
            filename: `stock-transfers-${periodSlug(period)}`,
            title: 'Stock transfers',
            subtitle: periodText(period),
            sheets: [{
              name: 'Transfers',
              columns: [
                { key: 'st_number', header: 'Transfer' }, { key: 'st_date', header: 'Date', type: 'date' },
                { key: 'from_godown', header: 'From' }, { key: 'to_godown', header: 'To' },
                { key: 'line_count', header: 'Lines', type: 'int' }, { key: 'vehicle_no', header: 'Vehicle' },
                { key: 'status', header: 'Status' }, { key: 'tally_sync_status', header: 'Tally' },
              ],
              rows: list.data.transfers,
            }],
          })} />
          <button className="btn" onClick={() => setCreating(!creating)}>{creating ? 'Close' : '＋ New transfer'}</button>
        </div>
      </div>

      <Alert onRetry={() => setError(null)}>{error}</Alert>
      <Alert onRetry={list.reload}>{list.error}</Alert>
      {creating && <NewTransfer session={session} onDone={() => { setCreating(false); list.reload(); }} setError={setError} />}
      {list.loading && !list.data && <CardSkeleton lines={4} />}

      {list.data && (
        <Card pad={list.data.transfers.length === 0}>
          {list.data.transfers.length === 0 ? (
            <Empty title="No transfers">Move a batch when stock is in the wrong godown for the order.</Empty>
          ) : (
            <div className="table-wrap" style={{ margin: 0, padding: 0 }}>
              <table>
                <thead><tr><th>Transfer</th><th>From</th><th>To</th><th className="r">Lines</th>
                  <th>Status</th><th>Tally</th><th /></tr></thead>
                <tbody>
                  {list.data.transfers.map((t) => (
                    <tr key={t.id} className="clickable" onClick={() => setOpen(open === t.id ? null : t.id)}>
                      <td className="mono">{t.st_number || 'draft'}<div className="sub">{day(t.st_date)}</div></td>
                      <td>{t.from_godown}</td>
                      <td>{t.to_godown}{t.vehicle_no && <div className="sub">{t.vehicle_no}</div>}</td>
                      <td className="r num">{t.line_count}</td>
                      <td><Pill value={t.status === 'in_transit' ? 'pending' : t.status === 'received' ? 'delivered' : t.status}
                        label={t.status.replace('_', ' ')} /></td>
                      <td><Pill value={t.tally_sync_status === 'manual' ? 'pending' : t.tally_sync_status}
                        label={t.tally_sync_status === 'manual' ? 'enter in Tally' : t.tally_sync_status} /></td>
                      <td className="r">
                        {t.status === 'draft' && (
                          <button className="btn small" onClick={(e) => { e.stopPropagation(); act(t.id, 'despatch'); }}>Despatch</button>
                        )}
                        {t.status === 'in_transit' && (
                          <button className="btn small" onClick={(e) => { e.stopPropagation(); act(t.id, 'receive'); }}>Receive</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {open && <TransferDetail id={open} onChanged={list.reload} />}
    </>
  );
}

// Marking the Tally stock journal as entered is the accounts side's call (routes/sync.js).
const CAN_MARK_TALLY = ['tenant_admin', 'accounts'];

function TransferDetail({ id, onChanged }) {
  const role = useSession().user.role;
  const t = useAsync(() => api.get(`/api/stock-transfers/${id}`), [id]);
  const [voucherRef, setVoucherRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [markError, setMarkError] = useState(null);

  const markEntered = async () => {
    setBusy(true); setMarkError(null);
    try {
      await api.post(`/api/sync/stock-transfers/${id}/entered-in-tally`,
        voucherRef.trim() ? { voucher_ref: voucherRef.trim() } : {});
      t.reload();
      if (onChanged) onChanged();
    } catch (e) { setMarkError(e.message); }
    finally { setBusy(false); }
  };
  if (t.loading && !t.data) return <CardSkeleton lines={3} />;
  if (t.error) return <Alert onRetry={t.reload}>{t.error}</Alert>;
  if (!t.data) return null;
  return (
    <Card title={`${t.data.st_number || 'Draft'} · ${t.data.from_godown} → ${t.data.to_godown}`}
      note={`${day(t.data.st_date)}${t.data.received_by_name ? ` · received by ${t.data.received_by_name}` : ''}`}>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Item</th><th>Batch</th><th>Expiry</th><th>Properties</th><th className="r">Quantity</th></tr></thead>
          <tbody>
            {t.data.lines.map((l) => (
              <tr key={l.id}>
                <td>{l.item_name}</td>
                <td className="mono">{l.batch_name}</td>
                <td>{day(l.expiry_date)}</td>
                <td><Pill value={l.spec_status} /></td>
                <td className="r num">{qty(l.qty, l.uom)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {t.data.tally_sync_status === 'manual' && (
        <Alert kind="warn">
          Record a stock journal in Tally for this movement. InvIT already reflects it, and the reconciliation will
          keep showing the difference until Tally carries it too.
        </Alert>
      )}
      {CAN_MARK_TALLY.includes(role) && ['in_transit', 'received'].includes(t.data.status)
        && t.data.tally_sync_status !== 'synced' && (
        <div className="row" style={{ gap: 8, alignItems: 'flex-end', marginTop: 12 }}>
          <Field label="Tally voucher no. (optional)">
            <input value={voucherRef} onChange={(e) => setVoucherRef(e.target.value)} maxLength={100} />
          </Field>
          <button className="btn small" disabled={busy} onClick={markEntered}>
            {busy ? 'Saving…' : 'Mark as entered in Tally'}
          </button>
        </div>
      )}
      {markError && <Alert kind="error">{markError}</Alert>}
    </Card>
  );
}

function NewTransfer({ session, onDone, setError }) {
  const entityId = session.entities[0] && session.entities[0].id;
  const [from, setFrom] = useState(session.godowns[0] ? session.godowns[0].id : '');
  const [to, setTo] = useState('');
  // Destinations come from the godown master rather than the session, whose
  // list is only the godowns this user works from; the destinations listing
  // names every godown, so an operator at one godown can still send stock on.
  const godownList = useAsync(() => api.get('/api/godowns?destinations=true'), []);
  const sourceEntity = (session.godowns.find((g) => g.id === from) || {}).entity_id;
  const destinations = (godownList.data ? godownList.data.godowns : session.godowns)
    .filter((g) => g.is_active !== false && g.id !== from && (!sourceEntity || !g.entity_id || g.entity_id === sourceEntity));
  React.useEffect(() => {
    if (!to && destinations[0]) setTo(destinations[0].id);
  }, [godownList.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const [vehicle, setVehicle] = useState('');
  const [rows, setRows] = useState([]);
  const [scan, setScan] = useState('');
  const [busy, setBusy] = useState(false);

  const stock = useAsync(() => (from
    ? api.get(`/api/batches?entity_id=${entityId}&godown_id=${from}&in_stock=true`)
    : Promise.resolve({ batches: [] })), [entityId, from]);

  // A new source godown means new stock: what was picked from the old one cannot
  // move from here, and the destination can never be the source itself.
  const changeFrom = (id) => {
    setFrom(id);
    setRows([]);
    if (to === id) {
      const other = (godownList.data ? godownList.data.godowns : session.godowns).find((g) => g.id !== id);
      setTo(other ? other.id : '');
    }
  };

  const addRow = (batch) => setRows((r) => (r.some((x) => x.batch_id === batch.id)
    ? r
    : [...r, { batch_id: batch.id, batch_name: batch.batch_name, item_name: batch.item_name, available: batch.qty_available, qty: '' }]));

  /** Picked from the list: the batch is already known by id, so nothing is looked up by name. */
  const pick = (batchId) => {
    const batch = stock.data && stock.data.batches.find((b) => b.id === batchId);
    if (batch) addRow(batch);
  };

  /** A barcode reader types the code and presses Enter; that is all this needs. */
  const lookup = async (code) => {
    if (!code.trim()) return;
    try {
      const batch = await api.get(`/api/batches/lookup?code=${encodeURIComponent(code)}&entity_id=${entityId}&godown_id=${from}`);
      addRow(batch);
      setScan('');
    } catch (e) { setError(e.message); }
  };

  const sameGodown = !to || from === to;

  const save = async (despatch) => {
    if (sameGodown) { setError('Choose a destination godown other than the source.'); return; }
    // A blank row is not moving; an unreadable quantity is an error, not a row
    // quietly left behind.
    const lines = [];
    for (const r of rows) {
      const n = parseNumber(r.qty);
      if (n === null) continue;
      if (!Number.isFinite(n) || n <= 0) { setError(`${r.batch_name}: the quantity to move must be a number above zero.`); return; }
      lines.push({ batch_id: r.batch_id, qty: n });
    }
    if (!lines.length) { setError('Enter a quantity to move on at least one batch.'); return; }
    setBusy(true); setError(null);
    try {
      await api.post('/api/stock-transfers', {
        from_godown_id: from, to_godown_id: to, vehicle_no: vehicle || undefined, despatch, lines,
      });
      onDone();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <Card title="New transfer">
      <div className="row">
        <Field label="From godown">
          <select value={from} onChange={(e) => changeFrom(e.target.value)}>
            {session.godowns.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </Field>
        <Field label="To godown">
          <select value={to} onChange={(e) => setTo(e.target.value)}>
            {!to && <option value="">Choose…</option>}
            {destinations.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </Field>
        <Field label="Vehicle"><input value={vehicle} onChange={(e) => setVehicle(e.target.value)} /></Field>
        <Field label="Scan a drum label" hint="or pick from the list below">
          <input value={scan} placeholder="scan or type a batch number"
            onChange={(e) => setScan(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookup(scan); } }} />
        </Field>
      </div>

      {rows.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Item</th><th>Batch</th><th className="r">Available</th>
              <th className="r" style={{ width: 150 }}>Move</th><th style={{ width: 52 }} /></tr></thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.batch_id}>
                  <td>{r.item_name}</td>
                  <td className="mono">{r.batch_name}</td>
                  <td className="r num">{qty(r.available)}</td>
                  <td className="r"><input className="num" style={{ textAlign: 'right' }} value={r.qty}
                    onChange={(e) => setRows(rows.map((x, i) => (i === idx ? { ...x, qty: e.target.value } : x)))} /></td>
                  <td className="r"><button className="btn small ghost"
                    onClick={() => setRows(rows.filter((_, i) => i !== idx))}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="inline" style={{ marginTop: 12 }}>
        <select value="" onChange={(e) => e.target.value && pick(e.target.value)} style={{ maxWidth: 340 }}>
          <option value="">Add a batch from {session.godowns.find((g) => g.id === from)?.name || 'this godown'}…</option>
          {stock.data && stock.data.batches.map((b) => (
            <option key={b.id} value={b.id}>{b.item_name} · {b.batch_name} · {qty(b.qty_available)} free</option>
          ))}
        </select>
        <button className="btn" disabled={busy || !rows.length || sameGodown} onClick={() => save(true)}>Despatch now</button>
        <button className="btn secondary" disabled={busy || !rows.length || sameGodown} onClick={() => save(false)}>Save as draft</button>
        {sameGodown && <span className="card-note">The destination must be a different godown.</span>}
      </div>
    </Card>
  );
}
